import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
  type AppUpdateInfo,
} from '@capawesome/capacitor-app-update'
import { appStoreUrl, playStoreUrl, FLAVOR_APPLE_APP_IDS } from 'src/flavors'

/**
 * Thin wrappers over the store-update plugin (@capawesome/capacitor-app-update) — see
 * useAppUpdate for the rules on WHEN anything is shown.
 *
 * ⚠️ The plugin exists only in a native build that lists it in src-capacitor/package.json
 * (it was missing there until 2026-10-29, so no app ever asked). Every call here is
 * guarded and best-effort: an update check must never break start-up.
 */

const FLAVOR = process.env.BUSINESS_TYPE || 'table'

export const isAndroid = () => Capacitor.getPlatform() === 'android'
export const hasStorePlugin = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AppUpdate')

/** The build date this app was shipped as (YYYYMMDD — Android versionName = iOS marketing
 *  version), or 0 when unknown. From @capacitor/app, so it works without the store plugin. */
export async function currentAppVersion(): Promise<number> {
  if (!Capacitor.isNativePlatform()) return 0
  try {
    const n = Number((await App.getInfo()).version)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

/** What the store offers, or null when it cannot be asked. */
export async function storeInfo(): Promise<AppUpdateInfo | null> {
  if (!hasStorePlugin()) return null
  try {
    return await AppUpdate.getAppUpdateInfo()
  } catch (err) {
    console.warn('[appUpdate] store lookup failed', err)
    return null
  }
}

export const updateAvailable = (info: AppUpdateInfo | null) =>
  !!info && info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE

/** Android already downloaded an update in the background — it only needs a restart. */
export const updateDownloaded = (info: AppUpdateInfo | null) =>
  !!info && info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED

/** Days the newer version has been out: Android's own staleness count, iOS's release date. */
export function daysAvailable(info: AppUpdateInfo | null): number {
  if (!info) return 0
  if (typeof info.clientVersionStalenessDays === 'number') return info.clientVersionStalenessDays
  const released = info.availableVersionReleaseDate ? Date.parse(info.availableVersionReleaseDate) : NaN
  return Number.isFinite(released) ? Math.floor((Date.now() - released) / 86400000) : 0
}

/**
 * The store page — through the plugin when present, else the flavor's store URL.
 *
 * ⚠️ Until 2026-11-03 the green button did NOTHING on either phone, and the REQUIRED screen
 * can't be closed — a raised `planner_app_min_version` locked people out (owner, 2026-11-03):
 *  · iOS: `openAppStore()` REQUIRES `appId` (the numeric Apple id) — without it the plugin
 *    rejects every time ("appId must be provided.").
 *  · The fallback was `window.open(url, '_blank')` AFTER an await: no longer a user tap, so
 *    WKWebView drops it as a popup, and Capacitor's Android WebView has no new-window handler.
 *    A plain NAVIGATION is not a popup: Capacitor hands an outside URL to the OS (Android:
 *    Bridge.launchIntent; iOS: decidePolicyFor → UIApplication.open), which opens the store.
 */
export async function openStore(): Promise<void> {
  const appleId = FLAVOR_APPLE_APP_IDS[FLAVOR]
  if (hasStorePlugin()) {
    try {
      // Android ignores appId (it opens its own package); iOS needs it.
      await AppUpdate.openAppStore(appleId ? { appId: appleId } : undefined)
      return
    } catch (err) {
      console.warn('[appUpdate] openAppStore failed, using the store URL', err)
    }
  }
  const url = isAndroid() ? playStoreUrl(FLAVOR) : appStoreUrl(FLAVOR) || playStoreUrl(FLAVOR)
  if (url) window.location.href = url
}

/** Google's full-screen update (Android only); anything else opens the store. */
export async function immediateUpdate(): Promise<void> {
  const info = await storeInfo()
  if (isAndroid() && updateAvailable(info) && info?.immediateUpdateAllowed) {
    try {
      await AppUpdate.performImmediateUpdate()
      return
    } catch (err) {
      console.warn('[appUpdate] immediate update failed, opening the store', err)
    }
  }
  await openStore()
}

/**
 * Android: download in the background while the person keeps working; `onDownloaded` fires
 * when it only needs a restart. Returns false when not possible (iOS, or Play refuses) —
 * the caller then opens the store instead.
 */
export async function flexibleUpdate(onDownloaded: () => void): Promise<boolean> {
  const info = await storeInfo()
  if (!isAndroid() || !updateAvailable(info) || !info?.flexibleUpdateAllowed) return false
  try {
    await AppUpdate.removeAllListeners()
    await AppUpdate.addListener('onFlexibleUpdateStateChange', (s) => {
      if (s.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) onDownloaded()
    })
    await AppUpdate.startFlexibleUpdate()
    return true
  } catch (err) {
    console.warn('[appUpdate] flexible update failed', err)
    return false
  }
}

/** Installs a downloaded Android update — the app restarts. */
export async function finishUpdate(): Promise<void> {
  try {
    await AppUpdate.completeFlexibleUpdate()
  } catch (err) {
    console.warn('[appUpdate] completing the update failed', err)
  }
}
